import { Option, options } from '~/components/Select';
import { recipientTypeOptions } from '~/constants/alcohol';
import { DistanceUnit } from '~/constants/distance';
import { apiUnitMap, WeightUnit } from '~/constants/weight';
import {
  AlcoholFormValues,
  DryIceFormValues,
  ExtrasFormValues,
  HazmatFormValues,
} from '~/pages/Orders/All/MultipleOrdersActions/AdditionalServicesForm';
import { getUserConfigDefaults } from '~/selectors/user.selectors';
import CarrierParcelTemplate from '~/types/CarrierParcelTemplate';
import { BatchEntryExtended } from '~/types/OrderBatchEntry';
import ParcelTemplate from '~/types/ParcelTemplate';

type Dimensions = {
  width: string;
  length: string;
  height: string;
  distanceUnit: Option;
};

export interface PackageTemplateChooserFormValues {
  weight: string;
  weightUnit: Option;
  parcelTemplate: Maybe<ParcelTemplate | CarrierParcelTemplate>;
  customDimensions: Maybe<Dimensions>;
  packageDimensionType?: Maybe<Option>;
  extras: {
    isUsingCustomDimensions: boolean;
    isWeightRequired?: boolean;
    carrierTemplateWithCustomDimensions: boolean;
  };
  useImportedOrderWeight: boolean;
  alcohol: AlcoholFormValues;
  dryIce: DryIceFormValues;
  isReturnRequested: boolean;
  hazmat: HazmatFormValues;
}

export function getInitialFormValues(
  templateData: Maybe<ParcelTemplate>,
  userConfigDefaults: ReturnType<typeof getUserConfigDefaults>,
  batchEntry?: Maybe<BatchEntryExtended>,
  initialExtrasValues?: ExtrasFormValues,
  useImportedWeight = false,
  initialWeight = '', // Parameter used to set an initial value to weight if the order has imported weight
): PackageTemplateChooserFormValues {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fallbackMassUnit = (userConfigDefaults as any).defaultMassUnit || WeightUnit.LB;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fallbackDistanceUnit = (userConfigDefaults as any).defaultDistanceUnit || DistanceUnit.IN;

  const inBatchMode = !batchEntry;
  const packageWeight = batchEntry?.parcel?.weight;
  const packageWeightUnit = batchEntry?.parcel?.weightUnit || WeightUnit.LB;

  const extrasInitialFormValues = {
    alcohol: initialExtrasValues?.alcohol || {
      containsAlcohol: false,
      recipientType: recipientTypeOptions[0], // Default to Consumer
    },
    dryIce: initialExtrasValues?.dryIce || {
      containsDryIce: false,
      weight: '',
      weightUnitId: apiUnitMap[userConfigDefaults.defaultMassUnit || WeightUnit.LB],
      packageWeight: !inBatchMode ? packageWeight : undefined,
      packageWeightUnit,
      inBatchMode,
    },
    hazmat: {
      containsHazmat: !!initialExtrasValues?.hazmat.containsHazmat,
      containsBiologicalMaterial: !!initialExtrasValues?.hazmat.containsBiologicalMaterial,
      containsLithiumBatteries: !!initialExtrasValues?.hazmat.containsLithiumBatteries,
    },
    isReturnRequested: initialExtrasValues?.isReturnRequested || false,
  };

  if (!templateData) {
    return {
      parcelTemplate: null,
      weight: isNaN(Number(initialWeight)) ? '' : initialWeight,
      weightUnit: options.commonWeightOptions[fallbackMassUnit],
      packageDimensionType: null,
      customDimensions: {
        width: '',
        length: '',
        height: '',
        distanceUnit: options.commonDistanceOptions[fallbackDistanceUnit],
      },
      extras: {
        isUsingCustomDimensions: true,
        carrierTemplateWithCustomDimensions: false,
      },
      useImportedOrderWeight: useImportedWeight,
      ...extrasInitialFormValues,
    };
  }

  return {
    // If objectId exists, this is a user template.
    // Otherwise if carrierTemplate, this is carrier template.
    // Otherwise, custom dimensions
    parcelTemplate: templateData.objectId ? templateData : templateData.carrierTemplate,
    weight: templateData.weight ? templateData.weight.toString() : '',
    weightUnit: options.commonWeightOptions[templateData.weightUnit || fallbackMassUnit],
    customDimensions: {
      width: templateData.width.toString(),
      length: templateData.length.toString(),
      height: templateData.height.toString(),
      distanceUnit:
        options.commonDistanceOptions[templateData.distanceUnit || fallbackDistanceUnit],
    },
    extras: {
      isUsingCustomDimensions: !(templateData.objectId || templateData.carrierTemplate),
      carrierTemplateWithCustomDimensions: !!(
        templateData.carrierTemplate &&
        templateData.carrierTemplate.isVariableDimensions &&
        !templateData.objectId
      ),
    },
    useImportedOrderWeight: useImportedWeight,
    ...extrasInitialFormValues,
  };
}
